import {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

import { useMsal } from "@azure/msal-react";

import { callApi } from "../api/api";

import { useNavigate } from "react-router-dom";

import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";

import useRBAC from "../hooks/useRBAC";
import { useBreakpoint } from "../hooks/useBreakpoint";

import {
  Users,
  ShieldCheck,
  Database,
  ClipboardList,
} from "lucide-react";

const isAssignmentActive = (status) =>
  (status || "Active").trim().toLowerCase() === "active";

export default function AdminDashboard() {
  const { instance, accounts } =
    useMsal();

  const navigate =
    useNavigate();

  const { hasAccess, isAdmin } =
    useRBAC();

  const [stats, setStats] =
    useState({
      users: 0,
      roles: 0,
      resources: 0,
      userRoles: 0,
      roleResources: 0,
    });

  const [recent, setRecent] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [sortConfig, setSortConfig] =
    useState({
      key: "Effective_From",
      direction: "desc",
    });

  const [page, setPage] =
    useState(1);

  const pageSize = 5;

  const [isNarrow, setIsNarrow] = useState(false);
  const [stackMainGrid, setStackMainGrid] = useState(false);
  const isCardView = useBreakpoint(1024);


  /* =========================================
     LOAD DASHBOARD
  ========================================= */

  const loadDashboard =
    useCallback(async () => {
      try {
        if (!accounts.length)
          return;

        setLoading(true);

        const res =
          await callApi(
            instance,
            accounts,
            "GET",
            "/api/admin/dashboard",
          );

        const data =
          res?.data?.data || {};

        setStats(
          data.stats || {},
        );

        setRecent(
          data.recentAssignments ||
            [],
        );
      } catch (err) {
        console.error(
          "Dashboard load failed",
          err,
        );
      } finally {
        setLoading(false);
      }
    }, [instance, accounts]);

  useEffect(() => {
    if (accounts.length) {
      loadDashboard();
    }
  }, [accounts.length, loadDashboard]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsNarrow(width <= 900);
      setStackMainGrid(width <= 1100);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* =========================================
     SORTING
  ========================================= */

  const sortedData =
    useMemo(() => {
      const sorted = [
        ...recent,
      ];

      sorted.sort(
        (a, b) => {
          const aVal =
            a[
              sortConfig.key
            ] || "";

          const bVal =
            b[
              sortConfig.key
            ] || "";

          if (aVal < bVal)
            return sortConfig.direction ===
              "asc"
              ? -1
              : 1;

          if (aVal > bVal)
            return sortConfig.direction ===
              "asc"
              ? 1
              : -1;

          return 0;
        },
      );

      return sorted;
    }, [recent, sortConfig]);

  /* =========================================
     PAGINATION
  ========================================= */

  const paginatedData =
    useMemo(() => {
      const start =
        (page - 1) *
        pageSize;

      return sortedData.slice(
        start,
        start + pageSize,
      );
    }, [sortedData, page]);

  const totalPages =
    Math.ceil(
      sortedData.length /
        pageSize,
    );

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
    requestAnimationFrame(() => {
      document.querySelector(".app-shell__content")?.scrollTo({ left: 0 });
      window.scrollTo({ left: 0 });
    });
  }, []);

  /* =========================================
     QUICK LINKS
  ========================================= */

  const quickLinks = [
    {
      label:
        "Manage Users",

      to: "/users",

      icon: Users,

      show:
        isAdmin ||
        hasAccess(
          "ADMIN",
          "READ",
        ),
    },

    {
      label:
        "Manage Roles",

      to: "/roles",

      icon: ShieldCheck,

      show:
        isAdmin ||
        hasAccess(
          "ADMIN",
          "READ",
        ),
    },

    {
      label:
        "Manage Resources",

      to: "/resources",

      icon: Database,

      show:
        isAdmin ||
        hasAccess(
          "ADMIN",
          "READ",
        ),
    },

    {
      label:
        "Access Review",

      to: "/access-review",

      icon: ClipboardList,

      show:
        isAdmin ||
        hasAccess(
          "ADMIN",
          "READ",
        ),
    },
  ];

  /* =========================================
     RESPONSIVE CARD MAPPERS
  ========================================= */

  const mapAssignmentCard = useCallback(
    (r) => {
      const active = isAssignmentActive(r.Status);

      return {
      id: `assignment-${r.user_name}-${r.role_name}-${r.Effective_From}`,
      icon: <Users size={16} />,
      tone: active ? "success" : undefined,
      title: r.user_name || "—",
      subtitle: r.role_name || "—",
      meta: [
        { label: "Effective From", value: formatDate(r.Effective_From) },
      ],
      badges: [
        { value: r.Status || "Active", tone: active ? "success" : undefined },
      ],
    };
    },
    [],
  );

  const mapAssignmentDrawer = useCallback(
    (r) => ({
      title: r.user_name || "—",
      fields: [
        { label: "Role", value: r.role_name || "—" },
        { label: "Effective From", value: formatDate(r.Effective_From) },
        { label: "Status", value: r.Status || "Active" },
      ],
    }),
    [],
  );

  const renderTable = useCallback(
    () => (
      <>
        <ScrollableTable style={styles.tableWrapper}>
          <table style={styles.table}>
            <colgroup>
              <col style={{ width: "28%" }} />
              <col style={{ width: "34%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "16%" }} />
            </colgroup>
            <thead>
              <tr>
                <th
                  style={{
                    ...styles.th,
                    ...(isNarrow ? styles.thMobile : {}),
                  }}
                  onClick={() => handleSort("user_name")}
                >
                  User ⇅
                </th>

                <th
                  style={{
                    ...styles.th,
                    ...(isNarrow ? styles.thMobile : {}),
                  }}
                  onClick={() => handleSort("role_name")}
                >
                  Role ⇅
                </th>

                <th
                  style={{
                    ...styles.th,
                    ...(isNarrow ? styles.thMobile : {}),
                  }}
                  onClick={() => handleSort("Effective_From")}
                >
                  Effective From ⇅
                </th>

                <th
                  style={{
                    ...styles.th,
                    ...(isNarrow ? styles.thMobile : {}),
                  }}
                >
                  Status
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.map((r, i) => (
                <tr key={i}>
                  <td
                    style={{
                      ...styles.td,
                      ...styles.userTd,
                      ...(isNarrow ? styles.tdMobile : {}),
                    }}
                    title={r.user_name}
                  >
                    {r.user_name}
                  </td>

                  <td
                    style={{
                      ...styles.td,
                      ...styles.roleTd,
                      ...(isNarrow ? styles.tdMobile : {}),
                    }}
                  >
                    <span style={styles.badge} title={r.role_name || "-"}>
                      {r.role_name || "-"}
                    </span>
                  </td>

                  <td
                    style={{
                      ...styles.td,
                      ...(isNarrow ? styles.tdMobile : {}),
                    }}
                  >
                    {formatDate(r.Effective_From)}
                  </td>

                  <td
                    style={{
                      ...styles.td,
                      ...(isNarrow ? styles.tdMobile : {}),
                    }}
                  >
                    <span
                      style={
                        isAssignmentActive(r.Status)
                          ? styles.active
                          : styles.inactive
                      }
                    >
                      ● {r.Status || "Active"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTable>
      </>
    ),
    [paginatedData, isNarrow, handleSort],
  );

  const assignmentPaginationProps =
    sortedData.length > 0
      ? {
          page,
          totalPages: totalPages || 1,
          totalRecords: sortedData.length,
          pageSize,
          currentPageCount: paginatedData.length,
          onPageChange: handlePageChange,
        }
      : null;

  /* =========================================
     LOADING
  ========================================= */

  if (loading) {
    return (
      <div
        style={
          styles.loader
        }
      >
        Loading dashboard...
      </div>
    );
  }

  return (
    <div
      className="page-container"
      style={
        styles.container
      }
    >
      {/* HEADER */}

      <div
        style={
          styles.header
        }
      >
        <div>
          <h1
            style={
              styles.title
            }
          >
            RBAC Administration
          </h1>

          <p
            style={
              styles.subtitle
            }
          >
            Manage users,
            roles,
            permissions,
            and access
            reviews.
          </p>
        </div>
      </div>

      {/* KPI */}

      <div
        style={
          styles.cardGrid
        }
      >
        <Card
          title="Users"
          value={
            stats.users
          }
        />

        <Card
          title="Roles"
          value={
            stats.roles
          }
        />

        <Card
          title="Resources"
          value={
            stats.resources
          }
        />

        <Card
          title="User Roles"
          value={
            stats.userRoles
          }
        />

        <Card
          title="Role Resources"
          value={
            stats.roleResources
          }
        />
      </div>

      {/* MAIN GRID */}

        <div
          style={{
            ...styles.mainGrid,
            ...(stackMainGrid ? styles.mainGridStack : {}),
          }}
        >
        {/* TABLE */}

        <div style={styles.mainGridColumn}>
            <div
              style={
                styles.card
              }
            >
          <div
            style={
              styles.cardHeader
            }
          >
            <div>
              <h3
                style={
                  styles.cardTitle
                }
              >
                Recent
                User-Role
                Assignments
              </h3>

              <p
                style={
                  styles.cardSub
                }
              >
                Latest RBAC
                access
                assignments
              </p>
            </div>

            <button
              style={
                styles.linkBtn
              }
              onClick={() =>
                navigate(
                  "/user-role-audit",
                )
              }
            >
              View All →
            </button>
          </div>

          <ResponsiveTableCards
            data={paginatedData}
            cardMapper={mapAssignmentCard}
            drawerMapper={mapAssignmentDrawer}
            renderTable={renderTable}
            loading={loading}
            emptyTitle="No recent assignments"
            emptySub="No user-role assignments found."
            pagination={assignmentPaginationProps || undefined}
          />
        </div>

        {!isCardView && assignmentPaginationProps && (
          <Pagination {...assignmentPaginationProps} />
        )}
        </div>

        {/* QUICK LINKS */}

        <div
          style={{
            ...styles.card,
            ...(isNarrow ? styles.cardMobile : {}),
          }}
        >
          <h3
            style={
              styles.cardTitle
            }
          >
            Quick Links
          </h3>

          <p
            style={
              styles.cardSub
            }
          >
            Administrative
            shortcuts
          </p>

          {quickLinks
            .filter(
              (l) => l.show,
            )
            .map(
              (
                link,
                i,
              ) => (
                <QuickLink
                  key={i}
                  isNarrow={
                    isNarrow
                  }
                  {...link}
                />
              ),
            )}
        </div>
      </div>
    </div>
  );
}

/* =========================================
   KPI CARD
========================================= */

function Card({ title,
  value,
}) {
  return (
    <div
      style={
        styles.kpiCard
      }
    >
      <div
        style={
          styles.kpiTitle
        }
      >
        {title}
      </div>

      <div
        style={
          styles.kpiValue
        }
      >
        {value}
      </div>
    </div>
  );
}

/* =========================================
   QUICK LINK
========================================= */

function QuickLink({ label,
  to,
  icon: Icon,
  isNarrow = false,
}) {
  const navigate =
    useNavigate();

  const [hover, setHover] =
    useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() =>
        navigate(to)
      }
      onMouseEnter={() =>
        setHover(true)
      }
      onMouseLeave={() =>
        setHover(false)
      }
      style={{
        ...styles.quickLink,
        ...(isNarrow ? styles.quickLinkMobile : {}),

        ...(hover
          ? styles.quickLinkHover
          : {}),
      }}
    >
      <div
        style={{
          ...styles.quickLeft,
          ...(isNarrow ? styles.quickLeftMobile : {}),
        }}
      >
        <Icon size={18} />

        <span>{label}</span>
      </div>

      <span
        style={{
          ...styles.arrow,

          transform: hover
            ? "translateX(4px)"
            : "translateX(0)",
        }}
      >
        →
      </span>
    </div>
  );
}

/* =========================================
   HELPERS
========================================= */

function formatDate(date) {
  if (!date) return "-";

  return new Date(
    date,
  ).toLocaleDateString();
}

const styles = {
  container: {
    padding: "clamp(12px, 4vw, 24px)",
    background:
      "#f8fafc",
    minHeight:
      "100vh",
    boxSizing: "border-box",
    maxWidth: "100%",
    // overflowX: "hidden",
  },

  loader: {
    padding: "24px",
  },

  header: {
    marginBottom:
      "24px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight:
      "700",
    color: "#0f172a",
  },

  subtitle: {
    marginTop: "6px",
    color: "#64748b",
    fontSize: "14px",
  },

  cardGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "clamp(12px, 3vw, 18px)",
    marginBottom:
      "24px",
  },

  kpiCard: {
    background:
      "#ffffff",
    border:
      "1px solid #e2e8f0",
    borderRadius:
      "14px",
    padding: "20px",
  },

  kpiTitle: {
    fontSize: "13px",
    color: "#64748b",
    marginBottom:
      "10px",
    textTransform:
      "uppercase",
    letterSpacing:
      "0.4px",
  },

  kpiValue: {
    fontSize: "30px",
    fontWeight:
      "700",
    color: "#0f172a",
  },

  mainGrid: {
    display: "grid",
    gridTemplateColumns:
      "2fr 1fr",
    gap: "clamp(14px, 3vw, 20px)",
    alignItems:
      "start",
    width: "100%",
    boxSizing: "border-box",
  },
  mainGridStack: {
    gridTemplateColumns: "1fr",
  },

  mainGridColumn: {
    minWidth: 0,
  },

  card: {
    background:
      "#ffffff",
    border:
      "1px solid #e2e8f0",
    borderRadius:
      "14px",
    padding: "clamp(16px, 3vw, 20px)",
    boxSizing: "border-box",
  },
  cardMobile: {
    padding: "16px",
  },

  cardHeader: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems:
      "flex-start",
    marginBottom:
      "14px",
    gap: "10px",
    flexWrap: "wrap",
  },

  cardTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight:
      "600",
    color: "#0f172a",
  },

  cardSub: {
    marginTop: "4px",
    color: "#64748b",
    fontSize: "13px",
  },

  linkBtn: {
    background:
      "transparent",
    border: "none",
    color: "#2563eb",
    cursor: "pointer",
    fontWeight:
      "600",
    whiteSpace: "nowrap",
  },

  tableWrapper: {
    overflowX: "auto",
    width: "100%",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    textAlign: "left",
    padding: "clamp(10px, 2vw, 14px)",
    background:
      "#f8fafc",
    color: "#475569",
    fontSize: "clamp(12px, 1.6vw, 13px)",
    borderBottom:
      "1px solid #e2e8f0",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "clamp(10px, 2vw, 14px)",
    borderBottom:
      "1px solid #f1f5f9",
    color: "#0f172a",
    fontSize: "clamp(13px, 2vw, 14px)",
    minWidth: 0,
  },
  thMobile: {
    whiteSpace: "normal",
    fontSize: "12px",
    padding: "10px",
  },
  tdMobile: {
    fontSize: "13px",
    padding: "10px",
  },

  userTd: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  roleTd: {
    minWidth: "140px",
  },

  badge: {
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "600",
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "middle",
  },

  active: {
    color: "#16a34a",
    fontWeight:
      "600",
  },

  inactive: {
    color: "#dc2626",
    fontWeight:
      "600",
  },

  pagination: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems:
      "center",
    marginTop:
      "18px",
  },

  pageBtn: {
    background:
      "#ffffff",
    border:
      "1px solid #cbd5e1",
    borderRadius:
      "8px",
    padding:
      "8px 14px",
    cursor: "pointer",
  },

  pageText: {
    fontSize: "14px",
    color: "#475569",
  },

  quickLink: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems:
      "center",
    border:
      "1px solid #e2e8f0",
    borderRadius:
      "12px",
    padding:
      "14px 16px",
    marginTop: "12px",
    cursor: "pointer",
    transition:
      "all 0.2s ease",
    background:
      "#ffffff",
  },
  quickLinkMobile: {
    padding: "12px 12px",
    flexWrap: "wrap",
    gap: "8px",
  },

  quickLinkHover: {
    background:
      "#f8fafc",
    transform:
      "translateX(4px)",
    borderColor:
      "#cbd5e1",
  },

  quickLeft: {
    display: "flex",
    alignItems:
      "center",
    gap: "10px",
    fontWeight:
      "500",
  },
  quickLeftMobile: {
    flexWrap: "wrap",
    gap: "8px",
  },

  arrow: {
    transition:
      "all 0.2s ease",
  },
};